import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    styled,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Records } from '../../../components/Records'
import { Request } from '../../../components/Request'
import { BlockWrapper } from '../../../components/Wrapper/BlockWrapper'
import { WrapperCenter } from '../../../components/WrapperCenter'
import { useAssistantAdministrationStore } from '../../../store/storeAssistantAdministration'
import { useWrapperStore } from '../../../store/storeWrapper'
import { theme } from '../../../theme'
import { SidebarType } from '../../../types/sidebars'
import { Meets } from '../MeetPage/components/Meets'
import CalendarMonthIcon from './../../../assets/icons/CalendarMonth.svg'
import FilmIcon from './../../../assets/icons/Film.svg?react'
import GearIcon from './../../../assets/icons/Gear.svg?react'
import MessageSquareChatIcon from './../../../assets/icons/MessageSquareChat.svg?react'
import ShieldCheck from './../../../assets/icons/ShieldCheck.svg?react'
import { ProjectItemMenu } from './components/ProjectItemMenu'

export const ProjectTitle = styled(Typography)(({ theme }) => ({
    color: '#1B1C1D',
    fontSize: '18px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '80%',
    display: 'inline-block',
    [theme.breakpoints.down('lg')]: {
        maxWidth: '72%',
    },
    [theme.breakpoints.down('md')]: {
        maxWidth: '62%',
    },
    [theme.breakpoints.down('sm')]: {
        maxWidth: '47%',
    },
}))

export const ProjectPage = ({ isWorkplaceArchived = false }) => {
    const { breakpoints } = useTheme()
    const [searchParams, setSearchParams] = useSearchParams()
    const { fetchMeeting } = useAssistantAdministrationStore((state) => state)
    const {
        fetchProjectDetail,
        errorFetchProjectDetail,
        projectDetail,
        openSidebarType,
        setOpenSidebarType,
    } = useWrapperStore((state) => state)

    const isMobile = useMediaQuery(breakpoints.down('md'))

    const handleFilterChange = (evt: SelectChangeEvent) => {
        searchParams.set('is_archived', evt.target.value)

        setSearchParams(searchParams)
    }

    useEffect(() => {
        fetchProjectDetail({ workplaceId: searchParams.get('workplaceId') })
        fetchMeeting({ workplace_id: searchParams.get('workplaceId') })
    }, [searchParams.get('workplaceId')])

    useEffect(() => {
        return () => {
            setOpenSidebarType(null)
        }
    }, [])

    const [value, setValue] = useState('1')

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue)
    }

    return (
        <BlockWrapper>
            <Stack
                sx={{
                    height: '100%',
                }}>
                {errorFetchProjectDetail && (
                    <WrapperCenter>
                        <Typography color="error" variant="h4">
                            Такого проекта не существует! Выберите другой проект из списка
                        </Typography>
                    </WrapperCenter>
                )}
                {projectDetail && (
                    <>
                        <Stack
                            flexDirection="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap="8px"
                            sx={{
                                padding: '24px 16px 16px 16px',
                                borderBottom: '1px solid #E9EAED',
                                height: '60px',
                            }}>
                            <ProjectTitle  variant="Header3">{projectDetail?.title}</ProjectTitle>
                            {projectDetail.is_protected && (
                                <Tooltip
                                    enterTouchDelay={0}
                                    title="«Защищённый проект» — все данные хранятся только на серверах ARHITEX в РФ, полностью принадлежащих и контролируемых компанией. При работе используются только защищённые нейросети в агрегаторе нейросетей">
                                    <IconButton sx={{ p: 0, width: '20px', height: '20px' }}>
                                        <ShieldCheck
                                            style={{
                                                color: '#5A5C5E',
                                                height: '16px',
                                                width: '16px',
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {projectDetail && (
                                <Stack spacing={1} direction="row" marginLeft="auto">
                                    <ProjectItemMenu workplaceDetail={projectDetail} />
                                    <Tooltip title="Контекст проекта">
                                        <IconButton
                                            sx={{
                                                '&:hover': { borderColor: '#F4F4F6' },
                                                background: '#F9FAFB',
                                                zIndex: 1000,
                                            }}
                                            color="primary"
                                            onClick={() => {
                                                setOpenSidebarType(
                                                    openSidebarType === SidebarType.PROJECT_SETTINGS
                                                        ? null
                                                        : SidebarType.PROJECT_SETTINGS,
                                                )
                                            }}>
                                            <GearIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Stack>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingRight: '16px',
                                flexWrap: 'wrap',
                            }}>
                            <Tabs
                                variant="scrollable"
                                scrollButtons={false}
                                value={value}
                                onChange={handleChange}
                                sx={{
                                    borderBottom: 'none',
                                    px: 2,
                                    minHeight: '80px',
                                }}>
                                <Tab
                                    variant="secondary"
                                    label={
                                        <Stack spacing={1} direction="row">
                                            <Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                    }}>
                                                    <MessageSquareChatIcon />
                                                    <Typography
                                                        variant={isMobile ? 'body2' : 'body1'}>
                                                        Запросы
                                                    </Typography>
                                                </Stack>
                                            </Typography>
                                        </Stack>
                                    }
                                    value="1"
                                />
                                <Tab
                                    variant="secondary"
                                    label={
                                        <Stack spacing={1} direction="row">
                                            <Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                    }}>
                                                    <FilmIcon />
                                                    <Typography
                                                        variant={isMobile ? 'body2' : 'body1'}>
                                                        Записи встреч
                                                    </Typography>
                                                </Stack>
                                            </Typography>
                                        </Stack>
                                    }
                                    value="0"
                                />
                                <Tab
                                    variant="secondary"
                                    label={
                                        <Stack spacing={1} direction="row">
                                            <Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                    }}>
                                                    <CalendarMonthIcon />
                                                    <Typography
                                                        variant={isMobile ? 'body2' : 'body1'}>
                                                        Встречи по календарю
                                                    </Typography>
                                                </Stack>
                                            </Typography>
                                        </Stack>
                                    }
                                    value="2"
                                />
                            </Tabs>
                            {value !== '2' ? (
                                <Select
                                    size="small"
                                    sx={{
                                        marginLeft: 'auto',
                                        [theme.breakpoints.down('sm')]: {
                                            '& .MuiSelect-select': {
                                                padding: '4px 16px',
                                            },
                                        },
                                    }}
                                    value={searchParams.get('is_archived') ?? ''}
                                    label="Статус"
                                    onChange={handleFilterChange}>
                                    <MenuItem value="false">Все</MenuItem>
                                    <MenuItem value="true">Архивные</MenuItem>
                                </Select>
                            ) : null}
                        </Box>

                        {value === '0' && <Records isWorkplaceArchived={isWorkplaceArchived} />}
                        {value === '1' && <Request isWorkplaceArchived={isWorkplaceArchived} />}
                        {value === '2' && <Meets />}
                    </>
                )}
            </Stack>
        </BlockWrapper>
    )
}
